import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "./Navbar/Navbar";
import { BsCheckCircleFill } from "react-icons/bs";
import { motion } from "framer-motion";
import { useGetUserByIdQuery } from "../Redux/auth/authApis";
import { useAuth } from "../hooks/useAuth";

const STEPS = [
  "Choose Module",
  "Select Certification",
  "Attend Quiz",
  "Google Review",
  "Download Certificate",
];

const CERTIFICATIONS = [
  { value: "ISO-9001 module-2", label: "ISO 9001 - Quality Management" },
  {
    value: "ISO-14001 module-2",
    label: "ISO 14001 - Environmental Management",
  },
  {
    value: "ISO-45001 module-2",
    label: "ISO 45001 - Occupational Health & Safety",
  },
  { value: "ISO-22000 module-2", label: "ISO 22000 - Food Safety Management" },
  {
    value: "ISO-27001 module-2",
    label: "ISO 27001 - Information Security Management",
  },
  {
    value: "ISO-22301 module-2",
    label: "ISO 22301 - Business Continuity Management Standard",
  },
  {
    value: "ISO-20000-1 module-2",
    label: "ISO 20000-1 - International Standard for Service Management",
  },
  {
    value: "ISO-27701 module-2",
    label: "ISO 27701 - Privacy Information Management",
  },
  { value: "ISO-13485 module-2", label: "ISO 13485 - Medical Devices" },
  {
    value: "ISO-17025 module-2",
    label: "ISO 17025 - Testing and Calibration Laboratories",
  },
  { value: "ISO-50001 module-2", label: "ISO 50001 - Energy Management" },
  { value: "GMP module-2", label: "GMP" },
  // { value: "HACCP module-2", label: "HACCP " },
  { value: "SOC-2 module-2", label: "SOC 2" },
  { value: "FSSC 22000 module-2", label: "FSSC Lead Auditor Training" },
  { value: "HACCP module-2", label: "HACCP Lead Implementer Training. " },
  { value: "GMP module-2", label: "GMP Lead Implementor  " },
  { value: "GDP module-2", label: "GDP lead auditor" },
  { value: "GDPR module-2", label: "GDPR Lead Auditor Training" },
  { value: "HIPAA module-2", label: "HIPAA" },
  {
    value: "27017 2015 module-2",
    label: "27017:2015 Lead Implemeter Training",
  },
  { value: "GLP module-2", label: "GLP" },
  {
    value: "ISO-41001 module-2",
    label: "ISO-41001  Facility Management System",
  },
  { value: "ISO-29001 module-2", label: "ISO 29001" },
  {
    value: "ISO-42001:2023 module-2",
    label: "ISO-42001:2023 Artificial Intelligence Management System",
  },
];

const Module2 = () => {
  const { userId } = useAuth();
  const { data: userData, isLoading, isError } = useGetUserByIdQuery(userId);
  const navigate = useNavigate();
  const location = useLocation();
  const [steps, setSteps] = useState(
    location.state?.steps || [false, false, false, false, false],
  );

  const handleCertificationSelect = (e) => {
    const selectedCertification = e.target.value;
    if (selectedCertification) {
      setSteps([true, true, false, false, false]);
      navigate(`/quiz/module2/${selectedCertification}`, {
        state: { steps: [true, true, false, false, false] },
      });
    }
  };

  const breadcrumbVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Navbar />

      <div className="relative flex-grow px-4 py-10">
        <div className="absolute inset-0 bg-gradient-to-br from-red-100/90 via-white/70 to-red-100/90 backdrop-blur-sm" />{" "}
        <div className="relative z-10 max-w-6xl mx-auto">
          {!isLoading && !isError && userData?.userDetails && (
            <div className="flex items-center -ml-8 mb-6">
              <motion.h1
                className="text-2xl sm:text-2xl md:text-3xl font-semibold text-gray-800"
                initial={{ opacity: 0, x: -80 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                Welcome,{" "}
                <span className="text-red-600 font-bold">
                  {userData.userDetails.firstName}{" "}
                  {userData.userDetails.lastName}
                </span>
              </motion.h1>
            </div>
          )}

          {/* STEPPER */}
          <div className="flex justify-center mt-8 mb-6 px-2">
            <div className="flex items-center bg-white/70 backdrop-blur-md shadow-xl rounded-full p-2 flex-wrap gap-2">
              {STEPS.map((stepLabel, index) => (
                <motion.div
                  key={index}
                  className="flex items-center"
                  initial="initial"
                  animate="animate"
                  variants={breadcrumbVariants}
                  transition={{ duration: 0.4, delay: index * 0.15 }}
                >
                  {/* STEP PILL */}
                  <div
                    className={`flex items-center px-4 py-2 rounded-full text-xs sm:text-sm md:text-base font-medium transition-all duration-300
                  ${
                    steps[index]
                      ? "bg-green-500 text-white"
                      : index === 0
                        ? "bg-gradient-to-r from-red-500 to-red-700 text-white shadow-md"
                        : "bg-gray-200 text-gray-600"
                  }`}
                  >
                    {/* STEP CIRCLE */}
                    <span
                      className={`mr-2 flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold
                    ${
                      steps[index]
                        ? "bg-white text-green-500"
                        : index === 0
                          ? "bg-white text-red-600"
                          : "bg-gray-300 text-gray-700"
                    }`}
                    >
                      {steps[index] ? <BsCheckCircleFill /> : index + 1}
                    </span>

                    {stepLabel}
                  </div>

                  {/* CONNECTOR LINE */}
                  {index < STEPS.length - 1 && (
                    <div className="hidden md:block w-6 h-[2px] bg-gray-300 mx-1"></div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 bg-white/95 backdrop-blur-md rounded-2xl min-h-[300px]  shadow-2xl p-6 sm:p-8">
            <div className="flex flex-col  justify-center">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 -mt-4">
                About This Internal Auditor{" "}
              </h1>

              <p className="text-m xs:text-base bg-gray-50 p-5 rounded-2xl text-gray-600 leading-relaxed">
                This module provides detailed information about the benefits
                <br /> and implementation of ISO certifications in various
                industries.
                <br /> ISO certifications ensure quality, efficiency, and safety
                in <br />
                operations and compliance with international standards.
              </p>
            </div>

            <div className="flex flex-col justify-center">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                ISO Certifications
              </h2>
              <select
                onChange={handleCertificationSelect}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 focus:outline-none bg-white text-gray-700 shadow-sm"
                defaultValue=""
                aria-label="Select ISO Certification"
              >
                <option value="" disabled>
                  Select ISO Certification
                </option>
                {CERTIFICATIONS.map((cert) => (
                  <option key={cert.value} value={cert.value}>
                    {cert.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Module2;
